import React, { useState, useEffect, useRef } from "react";
import { useTranslation } from 'react-i18next';
import axios from "../../axiosConfig";
import DeleteConfirmationModal from "../trainer/DeleteConfirmationModal";
import { FaCamera, FaTrash } from "react-icons/fa";
import { CustomDatePicker } from '../commons/datepicker';
import "../../styles/DogInfo.css";

const OwnerDogInfo = ({ dog, onUpdate }) => {
    const { t } = useTranslation(['dogs', 'common']);
    const [isEditing, setIsEditing] = useState(false);
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [uploadingPhoto, setUploadingPhoto] = useState(false);
    const [currentPhotoUrl, setCurrentPhotoUrl] = useState(dog?.photoUrl || null);
    const photoInputRef = useRef(null);
    const [editedDog, setEditedDog] = useState({
        name: "",
        breed: "",
        dateOfBirth: "",
        gender: "",
        approximate: false,
    });

    const formatGender = (gender) => {
        if (gender === "MALE") return t('dogs:male');
        if (gender === "FEMALE") return t('dogs:female');
        return gender || "";
    };

    useEffect(() => {
        if (dog) setCurrentPhotoUrl(currentPhotoUrl || null);
    }, [dog?.photoUrl]);

    // Initialize the edit state with the dog's current data
    useEffect(() => {
        if (dog) {
            setEditedDog({
                name: dog.name || "",
                breed: dog.breed || "",
                dateOfBirth: dog.dateOfBirth ? dog.dateOfBirth.substring(0, 10) : "",
                gender: dog.gender || "",
                approximate: dog.approximate || false,
            });
        }
    }, [dog]);

    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        setEditedDog((prev) => ({
            ...prev,
            [name]: type === "checkbox" ? checked : value,
        }));
    };

    const handleEdit = () => {
        setIsEditing(true);
    };

    const handleCancel = () => {
        // Reset the form values from the original dog info
        setEditedDog({
            name: dog.name || "",
            breed: dog.breed || "",
            dateOfBirth: dog.dateOfBirth ? dog.dateOfBirth.substring(0, 10) : "",
            gender: dog.gender || "",
            approximate: dog.approximate || false,
        });
        setIsEditing(false);
    };

    const handleSave = async () => {
        const command = {
            dogId: dog.id,
            name: editedDog.name,
            breed: editedDog.breed,
            dateOfBirth: editedDog.dateOfBirth,
            approximate: editedDog.approximate,
            gender: editedDog.gender,
        };

        try {
            const response = await axios.put("/api/v1/dog/edit", command);
            if (onUpdate) {
                onUpdate(response.data);
            }
            setIsEditing(false);
        } catch (error) {
            console.error("Error updating dog info:", error);
        }
    };

    const handleDeleteClick = () => {
        setIsDeleteModalOpen(true);
    };

    const handleDeleteConfirm = async () => {
        try {
            await axios.delete(`/api/v1/dog/delete/${dog.id}`);
            setIsDeleteModalOpen(false);
            window.location.reload();
        } catch (error) {
            console.error("Error deleting dog:", error);
            setIsDeleteModalOpen(false);
        }
    };

    const handleDeleteCancel = () => {
        setIsDeleteModalOpen(false);
    };

    const handlePhotoUpload = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;

        const allowedTypes = ["image/jpeg", "image/png", "image/webp"];
        if (!allowedTypes.includes(file.type)) {
            alert(t('dogs:photoAllowedFormats'));
            return;
        }
        if (file.size > 10 * 1024 * 1024) {
            alert(t('dogs:photoMaxSize'));
            return;
        }

        const formData = new FormData();
        formData.append("file", file);

        try {
            setUploadingPhoto(true);
            const response = await axios.post(`/api/v1/dog/${dog.id}/photo`, formData, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            setCurrentPhotoUrl(response.data.photoUrl);
            if (onUpdate) onUpdate(response.data);
        } catch (error) {
            console.error("Error uploading photo:", error);
            alert(t('dogs:photoUploadError'));
        } finally {
            setUploadingPhoto(false);
            if (photoInputRef.current) photoInputRef.current.value = "";
        }
    };

    const handlePhotoDelete = async () => {
        try {
            setUploadingPhoto(true);
            const response = await axios.delete(`/api/v1/dog/${dog.id}/photo`);
            setCurrentPhotoUrl(null);
            if (onUpdate) onUpdate(response.data);
        } catch (error) {
            console.error("Error deleting photo:", error);
        } finally {
            setUploadingPhoto(false);
        }
    };

    if (!dog) {
        return <p>{t('dogs:loadingDogData')}</p>;
    }

    return (
        <div className="owner-dog-info-section">
            <DeleteConfirmationModal
                isOpen={isDeleteModalOpen}
                onClose={handleDeleteCancel}
                onConfirm={handleDeleteConfirm}
                itemName={dog.name}
            />
            <div className="owner-dog-info-header">
                <h3>{t('dogs:dogInfoTitle')}</h3>
                <div className="owner-dog-actions in-header">
                    {isEditing ? (
                        <>
                            <button className="owner-zapisz-btn" onClick={handleSave}>{t('common:save')}</button>
                            <button className="owner-edit-dog-btn" onClick={handleCancel}>{t('common:cancel')}</button>
                        </>
                    ) : (
                        <>
                            <button className="owner-edit-dog-btn" onClick={handleEdit}>{t('common:edit')}</button>
                            <button className="owner-delete-dog-btn" onClick={handleDeleteClick}>{t('common:delete')}</button>
                        </>
                    )}
                </div>
            </div>

            {/* Photo left + Fields right */}
            <div className="owner-dog-hero">
                <div className="owner-dog-photo-side">
                    <div className="owner-dog-photo-container">
                        {currentPhotoUrl ? (
                            <img src={currentPhotoUrl} alt={dog.name} className="owner-dog-photo" />
                        ) : (
                            <div className="owner-dog-photo-placeholder">
                                <img src="/paw512.png" alt="Placeholder" className="owner-dog-paw-icon" />
                            </div>
                        )}
                    </div>
                    <div className="owner-dog-photo-actions">
                        <button
                            className="owner-dog-photo-btn"
                            onClick={() => photoInputRef.current?.click()}
                            disabled={uploadingPhoto}
                            type="button"
                        >
                            <FaCamera size={12} />
                            {currentPhotoUrl ? t('dogs:photoChange') : t('dogs:photoAdd')}
                        </button>
                        {currentPhotoUrl && (
                            <button
                                className="owner-dog-photo-btn owner-dog-photo-btn--delete"
                                onClick={handlePhotoDelete}
                                disabled={uploadingPhoto}
                                type="button"
                            >
                                <FaTrash size={12} />
                                {t('common:delete')}
                            </button>
                        )}
                    </div>
                    <input
                        ref={photoInputRef}
                        type="file"
                        accept="image/jpeg,image/png,image/webp"
                        onChange={handlePhotoUpload}
                        style={{ display: "none" }}
                    />
                </div>

                <div className="owner-dog-fields-side">
                    {isEditing ? (
                        <>
                            <div className="owner-dog-fields-grid">
                                <div className="owner-info-group">
                                    <label>{t('name')}</label>
                                    <input type="text" name="name" value={editedDog.name} onChange={handleInputChange} />
                                </div>
                                <div className="owner-info-group">
                                    <label>{t('breed')}</label>
                                    <input type="text" name="breed" value={editedDog.breed} onChange={handleInputChange} />
                                </div>
                                <div className="owner-info-group">
                                    <label>{t('dateOfBirth')}</label>
                                    <CustomDatePicker name="dateOfBirth" value={editedDog.dateOfBirth} onChange={handleInputChange} max={new Date().toISOString().split('T')[0]} />
                                </div>
                                <div className="owner-info-group">
                                    <label>{t('genderLabel')}</label>
                                    <input type="text" name="gender" value={editedDog.gender} onChange={handleInputChange} />
                                </div>
                            </div>
                            <div className="owner-info-group checkbox-group">
                                <input type="checkbox" name="approximate" checked={editedDog.approximate} onChange={handleInputChange} />
                                <label>{t('dogs:approximate')}</label>
                            </div>
                        </>
                    ) : (
                        <div className="owner-dog-fields-grid">
                            <div className="owner-info-group">
                                <label>{t('name')}</label>
                                <input type="text" value={dog.name || ""} disabled />
                            </div>
                            <div className="owner-info-group">
                                <label>{t('breed')}</label>
                                <input type="text" value={dog.breed || ""} disabled />
                            </div>
                            <div className="owner-info-group">
                                <label>{t('age')}</label>
                                <input type="text" value={dog.age || ""} disabled />
                            </div>
                            <div className="owner-info-group">
                                <label>{t('genderLabel')}</label>
                                <input type="text" value={formatGender(dog.gender)} disabled />
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default OwnerDogInfo;